import React, { useState, useEffect, useCallback } from 'react';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { useNavigate } from 'react-router-dom';
import { showSuccess, showError } from '../utils/toastUtils';
import '../stili/AggiornaEventi.css';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8080').replace(/\/$/, '');

export default function AggiornaEventi() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [eventi, setEventi] = useState([]);
  const [eventoInModifica, setEventoInModifica] = useState(null);
  const [statoFiltro, setStatoFiltro] = useState('TUTTI');
  const [formData, setFormData] = useState({
    titolo: '',
    descrizione: '',
    dataEvento: '',
    luogo: '',
    visibileHome: false,
  });

  const convertiDataInFormatoInput = (data) => {
    if (!data) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(data)) return data;

    const parsed = new Date(data);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0];
    }

    const parts = data.split('-');
    if (parts.length === 3) {
      const [giorno, mese, anno] = parts;
      return `${anno}-${mese}-${giorno}`;
    }

    return '';
  };

  const formatData = (data) => {
    if (!data) return '-';
    const parsed = new Date(data);
    if (Number.isNaN(parsed.getTime())) return data;
    return parsed.toLocaleDateString('it-IT');
  };

  const anteprimaDescrizione = (descrizione) => {
    if (!descrizione) return '-';
    return descrizione.length > 180 ? `${descrizione.substring(0, 180)}...` : descrizione;
  };

  const fetchEventi = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/admin/eventi`);

      if (!res.ok) {
        let msg = 'Errore nel recupero degli eventi';
        try {
          const err = await res.json();
          msg = err.message || msg;
        } catch {}
        throw new Error(msg);
      }

      const data = await res.json();
      setEventi(Array.isArray(data) ? data : []);
    } catch (err) {
      showError(err.message || 'Errore nel recupero degli eventi.');
      setEventi([]);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchEventi();
  }, [fetchEventi]);

  const handleEditClick = (evento) => {
    setEventoInModifica(evento.id);
    setFormData({
      titolo: evento.titolo || '',
      descrizione: evento.descrizione || '',
      dataEvento: convertiDataInFormatoInput(evento.dataEvento),
      luogo: evento.luogo || '',
      visibileHome: Boolean(evento.visibileHome),
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : name === 'titolo' || name === 'luogo'
          ? value.toUpperCase()
          : value,
    }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        titolo: (formData.titolo || '').trim(),
        descrizione: (formData.descrizione || '').trim(),
        luogo: (formData.luogo || '').trim(),
        visibileHome: Boolean(formData.visibileHome),
      };

      const res = await authFetch(`${API_URL}/api/admin/eventi/${eventoInModifica}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let msg = "Errore durante l'aggiornamento dell'evento";
        try {
          const err = await res.json();
          msg = err.message || msg;
        } catch {}
        throw new Error(msg);
      }

      showSuccess('Evento aggiornato con successo.');
      setEventoInModifica(null);
      setFormData({
        titolo: '',
        descrizione: '',
        dataEvento: '',
        luogo: '',
        visibileHome: false,
      });
      fetchEventi();
    } catch (err) {
      showError(err.message || "Errore durante l'aggiornamento.");
    }
  };

  const cambiaStato = async (idEvento, stato) => {
    try {
      const res = await authFetch(
        `${API_URL}/api/admin/eventi/${idEvento}/stato?stato=${stato}`,
        { method: 'PUT' }
      );

      if (!res.ok) {
        let msg = 'Errore aggiornamento stato evento';
        try {
          const err = await res.json();
          msg = err.message || msg;
        } catch {}
        throw new Error(msg);
      }

      showSuccess(`Stato evento aggiornato in ${stato}.`);
      fetchEventi();
    } catch (err) {
      showError(err.message || 'Errore durante il cambio stato.');
    }
  };

  const eventiFiltrati =
    statoFiltro === 'TUTTI'
      ? eventi
      : eventi.filter(
          (evento) => String(evento.stato || '').toUpperCase() === statoFiltro
        );

  return (
    <div className="aggeventi-container" translate="no">
      <h2 className="aggeventi-title">Gestione Eventi</h2>

      <div className="aggeventi-filter-card">
        <div className="aggeventi-filter-group">
          <label className="aggeventi-label" htmlFor="statoFiltro">
            Filtra per stato
          </label>
          <select
            id="statoFiltro"
            className="aggeventi-select"
            value={statoFiltro}
            onChange={(e) => setStatoFiltro(e.target.value)}
          >
            <option value="TUTTI">TUTTI</option>
            <option value="ATTIVO">ATTIVO</option>
            <option value="ANNULLATO">ANNULLATO</option>
            <option value="CONCLUSO">CONCLUSO</option>
          </select>
        </div>
      </div>

      {eventiFiltrati.length === 0 ? (
        <div className="aggeventi-empty-state">Nessun evento presente.</div>
      ) : (
        <div className="aggeventi-cards-grid">
          {eventiFiltrati.map((evento) => (
            <article className="aggeventi-card" key={evento.id}>
              <div className="aggeventi-card-header">
                <div className="aggeventi-card-title-box">
                  <h3 className="aggeventi-card-title">{evento.titolo}</h3>
                  <p className="aggeventi-card-date">
                    Data evento: {formatData(evento.dataEvento)}
                  </p>
                </div>
              </div>

              <div className="aggeventi-card-body">
                <div className="aggeventi-info-row">
                  <span className="aggeventi-info-label">Luogo</span>
                  <span className="aggeventi-info-value">{evento.luogo || '-'}</span>
                </div>

                <div className="aggeventi-info-row">
                  <span className="aggeventi-info-label">Stato</span>
                  <span className={`aggeventi-status-badge ${String(evento.stato || '').toLowerCase()}`}>
                    {evento.stato || '-'}
                  </span>
                </div>

                <div className="aggeventi-info-row">
                  <span className="aggeventi-info-label">Pubblicato in Home</span>
                  <span
                    className={`aggeventi-status-badge ${evento.visibileHome ? 'attivo' : 'annullato'}`}
                  >
                    {evento.visibileHome ? 'SI' : 'NO'}
                  </span>
                </div>

                <div className="aggeventi-content-box">
                  <span className="aggeventi-info-label">Descrizione</span>
                  <p className="aggeventi-content-preview">
                    {anteprimaDescrizione(evento.descrizione)}
                  </p>
                </div>
              </div>

              <div className="aggeventi-actions">
                <button
                  type="button"
                  className="aggeventi-btn-primary"
                  onClick={() => handleEditClick(evento)}
                >
                  Modifica
                </button>

                <button
                  type="button"
                  className="aggeventi-btn-success"
                  onClick={() => cambiaStato(evento.id, 'ATTIVO')}
                >
                  Attiva
                </button>

                <button
                  type="button"
                  className="aggeventi-btn-warning"
                  onClick={() => cambiaStato(evento.id, 'ANNULLATO')}
                >
                  Annulla
                </button>

                <button
                  type="button"
                  className="aggeventi-btn-secondary"
                  onClick={() => cambiaStato(evento.id, 'CONCLUSO')}
                >
                  Concludi
                </button>
              </div>

              {eventoInModifica === evento.id && (
                <div className="aggeventi-edit-card">
                  <h4 className="aggeventi-section-title">Modifica evento</h4>

                  <form onSubmit={handleUpdate}>
                    <div className="aggeventi-grid-2">
                      <div className="aggeventi-form-group">
                        <label className="aggeventi-label">Titolo</label>
                        <input
                          className="aggeventi-input"
                          type="text"
                          name="titolo"
                          value={formData.titolo}
                          onChange={handleChange}
                          placeholder="Titolo"
                          required
                        />
                      </div>

                      <div className="aggeventi-form-group">
                        <label className="aggeventi-label">Luogo</label>
                        <input
                          className="aggeventi-input"
                          type="text"
                          name="luogo"
                          value={formData.luogo}
                          onChange={handleChange}
                          placeholder="Luogo"
                          required
                        />
                      </div>

                      <div className="aggeventi-form-group">
                        <label className="aggeventi-label">Data evento</label>
                        <input
                          className="aggeventi-input"
                          type="date"
                          name="dataEvento"
                          value={formData.dataEvento}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="aggeventi-form-group">
                        <label className="aggeventi-label aggeventi-checkbox-label">
                          <input
                            type="checkbox"
                            name="visibileHome"
                            checked={formData.visibileHome}
                            onChange={handleChange}
                          />
                          <span>Pubblica in Home</span>
                        </label>
                      </div>

                      <div className="aggeventi-form-group aggeventi-full-width">
                        <label className="aggeventi-label">Descrizione</label>
                        <textarea
                          className="aggeventi-textarea"
                          name="descrizione"
                          value={formData.descrizione}
                          onChange={handleChange}
                          placeholder="Descrizione"
                          required
                        />
                      </div>
                    </div>

                    <div className="aggeventi-actions">
                      <button type="submit" className="aggeventi-btn-primary">
                        Salva
                      </button>

                      <button
                        type="button"
                        className="aggeventi-btn-secondary"
                        onClick={() => setEventoInModifica(null)}
                      >
                        Annulla
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      <div className="aggeventi-button-row">
        <button
          className="aggeventi-btn-secondary"
          type="button"
          onClick={() => navigate('/gestionale')}
        >
          Torna al Gestionale
        </button>
      </div>
    </div>
  );
}